import { useEffect, useState } from "react";
import { Tag } from "lucide-react";
import { toast } from "sonner";

import { BrandModal } from "@/components/common/BrandModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUpdateSearchProfile } from "@/hooks/mutations/useSearchProfileMutations";
import type { SearchProfile } from "@/types/domain";

export function EditProfileDialog({
  profile,
  open,
  onOpenChange,
}: {
  profile: SearchProfile;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const update = useUpdateSearchProfile();
  const [name, setName] = useState("");

  useEffect(() => {
    if (open) setName(profile.searchprofile_name);
  }, [open, profile.searchprofile_id]);

  function save() {
    if (!name.trim()) return;
    update.mutate(
      { id: profile.searchprofile_id, data: { searchprofile_name: name.trim() } },
      {
        onSuccess: () => {
          toast.success("Línea de negocio actualizada");
          onOpenChange(false);
        },
        onError: () => toast.error("No se pudo actualizar la línea de negocio"),
      },
    );
  }

  return (
    <BrandModal
      open={open}
      onOpenChange={onOpenChange}
      title="Editar línea de negocio"
      description="Modifica el nombre y la búsqueda general de esta línea de negocio."
      footer={
        <Button onClick={save} disabled={update.isPending || !name.trim()}>
          {update.isPending ? "Guardando..." : "Guardar Cambios"}
        </Button>
      }
    >
      <div className="space-y-1.5">
        <Label className="text-sm font-semibold">Nombre de la línea de negocio*</Label>
        <div className="relative">
          <Tag className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && save()}
            className="pl-9"
            autoFocus
          />
        </div>
      </div>
    </BrandModal>
  );
}
